/**
 * Утилиты для работы с датами на карточках канбана. Все сравнения — по суткам,
 * без учёта времени, чтобы «сегодня» не сдвигалось из-за часового пояса.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Нормализовать произвольную строку в YYYY-MM-DD или вернуть null. */
export function toIsoDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const t = value.trim();
  if (!t) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(t);
  return m ? m[1] : null;
}

/** Сегодняшняя дата в формате YYYY-MM-DD по локальному времени пользователя. */
export function todayIsoDay(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Разница в сутках между двумя YYYY-MM-DD (b − a). Положительная = `b` позже. */
export function diffDays(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const aDate = new Date(ay, am - 1, ad).getTime();
  const bDate = new Date(by, bm - 1, bd).getTime();
  return Math.round((bDate - aDate) / DAY_MS);
}

export type DueTone = "overdue" | "urgent" | "soon" | "ok" | "done" | "muted";

/**
 * Короткий человеко-читаемый чип «21 апр», «Сегодня», «Завтра», «Через 3 дн»,
 * «Просрочено на 5 дн», плюс тональность цвета под состояние.
 */
export function describeDue(
  dueDate: string | null | undefined,
  opts: { doneColumn?: boolean } = {},
): { label: string; tone: DueTone; short: string; iso: string } | null {
  const iso = toIsoDay(dueDate ?? null);
  if (!iso) return null;
  const today = todayIsoDay();
  const days = diffDays(today, iso);
  const short = formatShortRuDate(iso);

  if (opts.doneColumn) {
    return { iso, short, label: short, tone: "done" };
  }
  if (days < 0) {
    const n = Math.abs(days);
    return {
      iso,
      short,
      label: `Просрочено ${short} (${formatDayPlural(n, "на")})`,
      tone: "overdue",
    };
  }
  if (days === 0) return { iso, short, label: `Сегодня, ${short}`, tone: "urgent" };
  if (days === 1) return { iso, short, label: `Завтра, ${short}`, tone: "urgent" };
  if (days <= 3) return { iso, short, label: `${short} · через ${formatDayPlural(days)}`, tone: "soon" };
  return { iso, short, label: short, tone: "ok" };
}

/** Классификатор колонки по id/label — «сделано»?   Дублирует серверную логику. */
export function isDoneColumnId(columnLabel: string): boolean {
  const s = columnLabel.toLowerCase();
  return /(done|complete|completed|finished|closed|сделан|завершен|завершён|выполнен)/.test(s);
}

/** «21 апр», «21 апр 2027». Год показываем только если не текущий. */
export function formatShortRuDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const thisYear = new Date().getFullYear();
  const mon = date.toLocaleDateString("ru-RU", { month: "short" }).replace(".", "");
  if (y === thisYear) return `${d} ${mon}`;
  return `${d} ${mon} ${y}`;
}

/** Форма «дня / дня / дней». Если передан префикс вроде «на», подставит его. */
export function formatDayPlural(n: number, prefix = ""): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let form: "day" | "days_2_4" | "days";
  if (mod10 === 1 && mod100 !== 11) form = "day";
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) form = "days_2_4";
  else form = "days";
  const word = form === "day" ? "день" : form === "days_2_4" ? "дня" : "дней";
  return `${prefix ? prefix + " " : ""}${n} ${word}`;
}

export const DUE_TONE_STYLES: Record<DueTone, { bg: string; color: string; border: string }> = {
  overdue: { bg: "rgba(239, 68, 68, 0.12)", color: "#b91c1c", border: "rgba(239, 68, 68, 0.45)" },
  urgent: { bg: "rgba(245, 158, 11, 0.14)", color: "#b45309", border: "rgba(245, 158, 11, 0.45)" },
  soon: { bg: "rgba(234, 179, 8, 0.14)", color: "#854d0e", border: "rgba(234, 179, 8, 0.35)" },
  ok: { bg: "rgba(148, 163, 184, 0.18)", color: "#334155", border: "rgba(148, 163, 184, 0.45)" },
  done: { bg: "rgba(16, 185, 129, 0.14)", color: "#047857", border: "rgba(16, 185, 129, 0.45)" },
  muted: { bg: "rgba(148, 163, 184, 0.12)", color: "#64748b", border: "rgba(148, 163, 184, 0.25)" },
};
